import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera } from 'lucide-react';
import { useUploadReceipt } from '../hooks/useReceipts.js';
import ReceiptSheet from './ReceiptSheet.jsx';

export default function ReceiptCaptureButton({ variant = 'hero' }) {
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const upload = useUploadReceipt();

  useEffect(() => () => { if (photoUrl) URL.revokeObjectURL(photoUrl); }, [photoUrl]);

  const send = useCallback((file) => {
    setReceipt(null);
    upload.mutate(file, { onSuccess: setReceipt });
  }, [upload]);

  function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    fileRef.current = file;
    setPhotoUrl(URL.createObjectURL(file));
    setOpen(true);
    send(file);
  }

  function close() {
    setOpen(false);
    setReceipt(null);
    setPhotoUrl(null);
    upload.reset();
  }

  const trigger = () => inputRef.current?.click();

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        data-testid="receipt-file-input"
        onChange={handleFile}
      />
      {variant === 'fab' ? (
        // z-20 : reste sous la barre d'actions groupées (z-30) qui occupe la même zone.
        <button
          type="button"
          onClick={trigger}
          aria-label="Ajouter un ticket"
          className="fixed right-4 z-20 flex h-14 w-14 items-center justify-center rounded-full bg-slate-800 text-white shadow-lg active:scale-95 lg:hidden"
          style={{ bottom: 'calc(4.5rem + env(safe-area-inset-bottom))' }}
        >
          <Camera size={24} aria-hidden />
        </button>
      ) : (
        <button
          type="button"
          onClick={trigger}
          className="flex min-h-16 w-full items-center gap-3 rounded-2xl bg-slate-800 px-4 py-3 text-left text-white shadow-sm active:bg-slate-900"
        >
          <Camera size={28} aria-hidden className="shrink-0" />
          <span className="flex flex-col">
            <span className="text-base font-semibold">Ajouter un ticket</span>
            <span className="text-xs text-slate-300">Photo du ticket juste après avoir payé</span>
          </span>
        </button>
      )}
      <ReceiptSheet
        open={open}
        onClose={close}
        receipt={receipt}
        photoUrl={photoUrl}
        uploading={upload.isPending}
        uploadError={upload.error}
        onRetry={() => send(fileRef.current)}
      />
    </>
  );
}
